import { useCallback, useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";

import { GET_CONTENT_CATALOG } from "@/graphql/queries/admin";
import { Button } from "@/components/ui/button";
import { MultiSelect, type MultiOption } from "@/components/admin/multi-select";

/**
 * The filters the content screens share.
 *
 * Every one of the five is an independent list, and every one may be empty. There
 * is no cascade where picking a subject *sets* a grade: a grade with no subject
 * ("show me everything fifth grade") and a subject with no grade ("all of
 * physics") are both ordinary questions, and the pages used to be able to ask
 * neither — subject and grade were single-valued and a subject change overwrote
 * the grade.
 *
 * What does depend on what is only the *options*: concepts belong to skills, so
 * there is nothing to offer until a skill is picked.
 */
export interface ContentFilter {
  subjects: string[];
  grades: number[];
  skills: string[];
  levels: number[];
  concepts: string[];
}

export const EMPTY_FILTER: ContentFilter = {
  subjects: [],
  grades: [],
  skills: [],
  levels: [],
  concepts: [],
};

export interface CatalogConcept {
  key: string;
  name: string;
  levels: number[] | null;
}

export interface CatalogSkill {
  id: string;
  name: string;
  grade: number;
  subject: string;
  subjectName: string;
  concepts: CatalogConcept[];
}

export interface ContentCatalog {
  subjects: { id: string; name: string }[];
  skills: CatalogSkill[];
  loading: boolean;
}

interface CatalogData {
  subjects: {
    id: string;
    name: string;
    skills: { id: string; name: string; grade: number | null; concepts: CatalogConcept[] }[];
  }[];
}

/** Every subject, skill and concept the config declares. */
export function useContentCatalog(): ContentCatalog {
  const { data, loading } = useQuery<CatalogData>(GET_CONTENT_CATALOG);

  return useMemo(() => {
    const subjects = (data?.subjects || []).map((subject) => ({
      id: subject.id,
      name: subject.name,
    }));
    const skills = (data?.subjects || []).flatMap((subject) =>
      subject.skills.map((skill) => ({
        id: skill.id,
        name: skill.name,
        grade: skill.grade ?? 1,
        subject: subject.id,
        subjectName: subject.name,
        concepts: skill.concepts || [],
      })),
    );

    return { subjects, skills, loading };
  }, [data, loading]);
}

const LEVEL_NAMES: Record<number, string> = { 1: "Lengvas", 2: "Vidutinis", 3: "Sunkus" };

function readList(params: URLSearchParams, plural: string, singular: string): string[] {
  // Both spellings are read, and that is not tidiness: links arrive from outside
  // these pages carrying the singular form — the flagged queue's „Tvarkyti
  // teoriją", the generators' way back, the session page's „Eiti į admin" — and a
  // reader that only knew `subjects` would drop the one thing those links say.
  const raw = params.get(plural) ?? params.get(singular);
  if (!raw) return [];

  return raw
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value.length > 0 && value !== "all");
}

function readNumbers(params: URLSearchParams, plural: string, singular: string): number[] {
  return readList(params, plural, singular)
    .map((value) => parseInt(value, 10))
    .filter((value) => Number.isFinite(value));
}

/**
 * Filter state, in the URL.
 *
 * In the URL rather than in React state so a filtered view can be sent to
 * somebody, reloaded, and arrived at from another screen — which is how these
 * pages are reached in the first place.
 */
export function useContentFilter(): {
  filter: ContentFilter;
  setFilter: (patch: Partial<ContentFilter>) => void;
  clearFilter: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = useMemo<ContentFilter>(
    () => ({
      subjects: readList(searchParams, "subjects", "subject"),
      grades: readNumbers(searchParams, "grades", "grade"),
      skills: readList(searchParams, "skills", "skill"),
      levels: readNumbers(searchParams, "levels", "level"),
      concepts: readList(searchParams, "concepts", "concept"),
    }),
    [searchParams],
  );

  const setFilter = useCallback(
    (patch: Partial<ContentFilter>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const write = (plural: string, singular: string, values: (string | number)[]) => {
            next.delete(singular);
            if (values.length === 0) next.delete(plural);
            else next.set(plural, values.join(","));
          };

          if (patch.subjects) write("subjects", "subject", patch.subjects);
          if (patch.grades) write("grades", "grade", patch.grades);
          if (patch.skills) write("skills", "skill", patch.skills);
          if (patch.levels) write("levels", "level", patch.levels);
          if (patch.concepts) write("concepts", "concept", patch.concepts);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilter = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        ["subject", "subjects", "grade", "grades", "skill", "skills", "level", "levels",
          "concept", "concepts"].forEach((key) => next.delete(key));
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return { filter, setFilter, clearFilter };
}

/** The skills a filter selects, in reading order. */
export function filterSkills(catalog: ContentCatalog, filter: ContentFilter): CatalogSkill[] {
  return catalog.skills
    .filter((skill) => filter.subjects.length === 0 || filter.subjects.includes(skill.subject))
    .filter((skill) => filter.grades.length === 0 || filter.grades.includes(skill.grade))
    .filter((skill) => filter.skills.length === 0 || filter.skills.includes(skill.id))
    .sort(
      (a, b) =>
        a.subject.localeCompare(b.subject) ||
        a.grade - b.grade ||
        a.name.localeCompare(b.name, "lt"),
    );
}

/** True when the question's concepts satisfy the concept filter. */
export function matchesConcepts(filter: ContentFilter, concepts: string[]): boolean {
  if (filter.concepts.length === 0) return true;
  if (filter.concepts.includes(UNTAGGED) && concepts.length === 0) return true;

  return concepts.some((key) => filter.concepts.includes(key));
}

/** „Be sąvokos" as a concept value: questions nobody could attribute. */
export const UNTAGGED = "__untagged__";

export function ContentFilterBar({
  catalog,
  filter,
  setFilter,
  clearFilter,
  /** Concepts are per skill, so they are offered only once a skill is picked. */
  showConcepts = true,
  showLevels = true,
  /** Off where the page already navigates by skill — a second control doing the
   *  same thing is a second thing to keep in agreement. */
  showSkills = true,
  /** Whose concepts to offer when the skill picker is not the thing choosing the
   *  skill. Defaults to the skills the filter selects. */
  conceptSkills,
  /** Anything the page wants on the same row — a count, a view toggle. */
  children,
}: {
  catalog: ContentCatalog;
  filter: ContentFilter;
  setFilter: (patch: Partial<ContentFilter>) => void;
  clearFilter: () => void;
  showConcepts?: boolean;
  showLevels?: boolean;
  showSkills?: boolean;
  conceptSkills?: string[];
  children?: React.ReactNode;
}) {
  const subjectOptions: MultiOption[] = catalog.subjects.map((subject) => ({
    value: subject.id,
    label: subject.name,
  }));

  // Grades narrow to the subjects in play, and skills to both — the options are
  // what depends on the other filters, never the selection. Picking a grade with
  // no subject still offers every grade any subject has.
  const gradeOptions: MultiOption[] = useMemo(() => {
    const inScope = catalog.skills.filter(
      (skill) => filter.subjects.length === 0 || filter.subjects.includes(skill.subject),
    );
    return [...new Set(inScope.map((skill) => skill.grade))]
      .sort((a, b) => a - b)
      .map((grade) => ({ value: String(grade), label: `${grade} klasė` }));
  }, [catalog.skills, filter.subjects]);

  const skillOptions: MultiOption[] = useMemo(
    () =>
      catalog.skills
        .filter(
          (skill) => filter.subjects.length === 0 || filter.subjects.includes(skill.subject),
        )
        .filter((skill) => filter.grades.length === 0 || filter.grades.includes(skill.grade))
        .sort(
          (a, b) =>
            a.subject.localeCompare(b.subject) ||
            a.grade - b.grade ||
            a.name.localeCompare(b.name, "lt"),
        )
        .map((skill) => ({
          value: skill.id,
          label: skill.name,
          // Two subjects both have a „Kampai", and two grades both have
          // „Daugyba" — without this the list reads as duplicates.
          hint: `${skill.subjectName} · ${skill.grade} kl. · ${skill.id}`,
        })),
    [catalog.skills, filter.subjects, filter.grades],
  );

  // Only the picked skills' concepts: a union across 650 skills would be
  // thousands of options, most of them meaningless next to each other.
  // Memoized on its own contents rather than its identity: the caller composes
  // this list per render, and a fresh array every time would rebuild the options
  // for 650 skills on every keystroke elsewhere on the page.
  const scopeKey = (conceptSkills?.length ? conceptSkills : filter.skills).join(",");
  const conceptOptions: MultiOption[] = useMemo(() => {
    const conceptScope = scopeKey ? scopeKey.split(",") : [];
    const picked = catalog.skills.filter((skill) => conceptScope.includes(skill.id));
    const options = picked.flatMap((skill) =>
      skill.concepts.map((concept) => ({
        value: concept.key,
        label: concept.name,
        hint: picked.length > 1 ? skill.name : concept.key,
      })),
    );

    const seen = new Set<string>();
    const unique = options.filter((option) =>
      seen.has(option.value) ? false : (seen.add(option.value), true),
    );

    // Untagged is a finding rather than an absence — it is what
    // ConceptCoverageFlagger raises a theory_gap on — so it is selectable.
    return unique.length > 0
      ? [...unique, { value: UNTAGGED, label: "Be sąvokos", hint: "Nepriskirta jokiai sąvokai" }]
      : [];
  }, [catalog.skills, scopeKey]);

  const levelOptions: MultiOption[] = [1, 2, 3].map((level) => ({
    value: String(level),
    label: `${level} — ${LEVEL_NAMES[level]}`,
  }));

  // Picking a grade that excludes an already-picked skill would otherwise leave a
  // filter selecting nothing, with the reason two dropdowns apart. Pruning keeps
  // the two consistent; the concepts of a dropped skill go with it.
  const pruneSkills = (patch: Partial<ContentFilter>) => {
    const next = { ...filter, ...patch };
    const allowed = new Set(
      catalog.skills
        .filter((skill) => next.subjects.length === 0 || next.subjects.includes(skill.subject))
        .filter((skill) => next.grades.length === 0 || next.grades.includes(skill.grade))
        .map((skill) => skill.id),
    );
    const skills = next.skills.filter((key) => allowed.has(key));
    if (skills.length === next.skills.length) return patch;

    const keptConcepts = new Set(
      catalog.skills
        .filter((skill) => skills.includes(skill.id))
        .flatMap((skill) => skill.concepts.map((concept) => concept.key)),
    );
    return {
      ...patch,
      skills,
      concepts: filter.concepts.filter(
        (key) => key === UNTAGGED || keptConcepts.has(key),
      ),
    };
  };

  const active =
    filter.subjects.length +
    filter.grades.length +
    filter.skills.length +
    filter.levels.length +
    filter.concepts.length;

  return (
    <div className="flex flex-wrap items-end gap-3">
      <MultiSelect
        label="Dalykas"
        className="w-48"
        options={subjectOptions}
        selected={filter.subjects}
        onChange={(subjects) => setFilter(pruneSkills({ subjects }))}
        allLabel="Visi dalykai"
      />
      <MultiSelect
        label="Klasė"
        className="w-40"
        options={gradeOptions}
        selected={filter.grades.map(String)}
        onChange={(grades) =>
          setFilter(pruneSkills({ grades: grades.map((value) => parseInt(value, 10)) }))
        }
        allLabel="Visos klasės"
        searchable={gradeOptions.length > 12}
      />
      {showSkills && (
      <MultiSelect
        label="Įgūdis"
        className="w-64"
        options={skillOptions}
        selected={filter.skills}
        onChange={(skills) => {
          const keptConcepts = new Set(
            catalog.skills
              .filter((skill) => skills.includes(skill.id))
              .flatMap((skill) => skill.concepts.map((concept) => concept.key)),
          );
          setFilter({
            skills,
            concepts: filter.concepts.filter(
              (key) => key === UNTAGGED || keptConcepts.has(key),
            ),
          });
        }}
        allLabel="Visi įgūdžiai"
        searchable
      />
      )}
      {showLevels && (
        <MultiSelect
          label="Lygis"
          className="w-44"
          options={levelOptions}
          selected={filter.levels.map(String)}
          onChange={(levels) =>
            setFilter({ levels: levels.map((value) => parseInt(value, 10)) })
          }
          allLabel="Visi lygiai"
        />
      )}
      {showConcepts && (
        <MultiSelect
          label="Sąvoka"
          className="w-56"
          options={conceptOptions}
          selected={filter.concepts}
          onChange={(concepts) => setFilter({ concepts })}
          allLabel="Visos sąvokos"
          emptyLabel={showSkills ? "Pasirink įgūdį" : "Nėra sąvokų"}
          searchable={conceptOptions.length > 12}
        />
      )}

      {active > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-9 text-xs text-muted-foreground"
          onClick={clearFilter}
        >
          <X className="h-3.5 w-3.5 mr-1" />
          Išvalyti filtrus
        </Button>
      )}

      {children}
    </div>
  );
}
